'use client';

import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import { SearchBar } from '@/components/ui/search';
import { openBettyChat } from '@/shared/utils/externalLinks';

/**
 * Navigation variant types:
 * - 'back-help': Back button + Help button (checkout, order flows)
 * - 'back-search': Back button + Search bar (product detail, category pages)
 * - 'search-only': Just search bar (homepage, general browsing)
 */
export type HeaderSecondaryNavVariant = 'back-help' | 'back-search' | 'search-only';

interface HeaderSecondaryNavProps {
  /**
   * The navigation variant to display
   */
  variant: HeaderSecondaryNavVariant;
  /**
   * Whether the nav should be visible (controlled by scroll direction)
   */
  isVisible: boolean;
  /**
   * Custom back button handler (optional - defaults to router.back())
   */
  onBack?: () => void;
  /**
   * Custom help button handler (optional - defaults to WhatsApp)
   */
  onHelp?: () => void;
  /**
   * Whether to show the horizontal divider below the nav
   * @default true for 'back-help', false for others
   */
  showDivider?: boolean;
}

/**
 * =============================================================================
 * HEADER SECONDARY NAVIGATION COMPONENT
 * =============================================================================
 *
 * A reusable component for the secondary navigation row in the Header.
 * Supports three variants for different page types:
 *
 * 1. BACK-HELP (Transactional Pages)
 *    - Large back button on the left
 *    - Large help button (filled) on the right
 *    - Used for: Checkout, Order details, Payment flows
 *    - Shows horizontal divider
 *
 * 2. BACK-SEARCH (Browse Pages)
 *    - Back button on the left
 *    - Search bar fills remaining space
 *    - Used for: Product detail, Category pages
 *
 * 3. SEARCH-ONLY (Homepage/General)
 *    - Full-width search bar
 *    - Used for: Homepage, general browsing
 *
 * FEATURES:
 * - Mobile only (md:hidden)
 * - Collapsible animation (max-height + opacity transition)
 * - Consistent styling across all variants
 * - Configurable handlers for back/help actions
 *
 * USAGE IN HEADER:
 * ```tsx
 * <HeaderSecondaryNav
 *   variant="back-help"
 *   isVisible={showMobileNav}
 * />
 * ```
 *
 * =============================================================================
 */
function HeaderSecondaryNav({
  variant,
  isVisible,
  onBack,
  onHelp,
  showDivider,
}: HeaderSecondaryNavProps) {
  const router = useRouter();

  // Default back handler
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      router.back();
    }
  };

  // Default help handler - opens WhatsApp chat with Betty
  const handleHelp = () => {
    if (onHelp) {
      onHelp();
    } else {
      openBettyChat();
    }
  };

  // Determine if divider should show (default true for back-help and back-search)
  const shouldShowDivider = showDivider ?? (variant === 'back-help' || variant === 'back-search');

  return (
    <div className="md:hidden">
      {/* Animated container for buttons */}
      <div
        className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${isVisible
            ? 'mt-4 max-h-24 opacity-100'
            : 'mt-0 max-h-0 opacity-0 invisible pointer-events-none'
          }
        `}
      >
        {/* Navigation content based on variant */}
        <div className="flex w-full items-center gap-3 pb-3">
          {/* Back Button - shown for back-help and back-search variants (size-10 = 40px to match PageNavigationBar) */}
          {(variant === 'back-help' || variant === 'back-search') && (
            <button
              type="button"
              onClick={handleBack}
              className="flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white"
              aria-label="Go back"
            >
              <Image
                src="/assets/svgs/Icons/ArrowLeft_nav.svg"
                alt="Back"
                width={20}
                height={20}
              />
            </button>
          )}

          {/* Search Bar - shown for back-search and search-only variants */}
          {(variant === 'back-search' || variant === 'search-only') && (
            <SearchBar className="flex-1" />
          )}

          {/* Spacer - for back-help variant to push help button to right */}
          {variant === 'back-help' && <div className="flex-1" />}

          {/* Help Button - shown for back-help variant (size-10 = 40px to match PageNavigationBar) */}
          {variant === 'back-help' && (
            <button
              type="button"
              onClick={handleHelp}
              className="flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white"
              aria-label="Get help"
            >
              <Image
                src="/assets/svgs/Icons/Question_fill_nav.svg"
                alt="Help"
                width={20}
                height={20}
              />
            </button>
          )}
        </div>
      </div>

      {/* Horizontal divider - OUTSIDE overflow container, edge-to-edge */}
      {shouldShowDivider && (
        <div
          className={`
            relative left-1/2 h-px w-screen -translate-x-1/2 bg-gray-200 transition-opacity duration-300
            ${isVisible ? 'opacity-100' : 'opacity-0'}
          `}
        />
      )}
    </div>
  );
}

export default HeaderSecondaryNav;
